"use client";

import * as stylex from "@stylexjs/stylex";
import { ArrowRight, Image, ImageOff, ImagePlus, Smartphone, Upload, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { copy } from "@/copy";
import type { Photo } from "@/domain/schemas";
import type { ZoneState } from "@/domain/zoneState";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, motion, radii, shadows, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { IconButton } from "@/ui/IconButton";
import { press } from "@/ui/press";
import { QrCode } from "@/ui/QrCode";
import { Skeleton } from "@/ui/Skeleton";
import { useBoxSize } from "@/ui/useBoxSize";
import { Zone } from "@/ui/Zone";
import type { FirstUpload, PhotoText } from "../todaysPhoto";
export type PhotoThumb = {
  id: string;
  src: string;
};
export type ShownPhoto = {
  photo: Photo;
  src: string;
  text: PhotoText;
  others: readonly PhotoThumb[];
};
export type QrShown = {
  url?: string;
};
type TodaysPhotoProps = {
  state: ZoneState<ShownPhoto>;
  upload?: FirstUpload;
  qr?: QrShown;
  online: boolean;
  fromPhone: boolean;
  moments: string;
  defaultFailed?: boolean;
  frame?: stylex.StyleXStyles;
  onAdd: () => void;
  onChooseFiles: () => void;
  onCloseQr: () => void;
  onOpen: (id: string) => void;
  onOpenPhotos: () => void;
  xstyle?: stylex.StyleXStyles;
};
type ActionsProps = {
  narrow: boolean;
  all: boolean;
  onAdd: () => void;
  onOpenPhotos: () => void;
};
type PhotoBodyProps = {
  shown: ShownPhoto;
  narrow: boolean;
  online: boolean;
  defaultFailed: boolean;
  frame?: stylex.StyleXStyles;
  onOpen: (id: string) => void;
  onOpenPhotos: () => void;
};
type FrameImageProps = {
  src: string;
  onError: () => void;
};
type MoreRowProps = {
  others: readonly PhotoThumb[];
  onOpenPhotos: () => void;
};
type FirstUploadProps = {
  upload: FirstUpload;
  frame?: stylex.StyleXStyles;
  onOpenPhotos: () => void;
};
type NoPhotosProps = {
  fromPhone: boolean;
  moments: string;
  onAdd: () => void;
};
type QrPanelProps = {
  qr: QrShown;
  onChooseFiles: () => void;
  onCloseQr: () => void;
};
const text = copy.today.photo;
const NARROW_BOX = 440;
const MAX_THUMBS = 3;
const MIN_BAR_PERCENT = 2;
const PORTRAIT_POSITION = "50% 30%";
const fadeIn = "x18re5ia-B";
const _temp = {
  kzqmXN: "x5lhr3w",
  "$$css": true
};
const styles = {
  panel: {
    kWkggS: "xh540kj",
    kaIpWk: "xwe7p7i",
    kmVPX3: "x1vvitgl",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1hu4l6k",
    k7Eaqz: "xeuugli",
    kAzted: "x2lwn1j",
    kVQacm: "xb3r6kr",
    $$css: true
  },
  iconButton: {
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    $$css: true
  },
  tap: {
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kmVPX3: "x1717udv",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11xpdln",
    kIyJzY: "x1bpocu2",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  photo: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1hu4l6k",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  frame: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x1lliihq",
    kzqmXN: "xh8yej3",
    kOBAk4: "x1dltgaz",
    kaIpWk: "x1fv5t7x",
    kVQacm: "xb3r6kr",
    kWkggS: "x5r8ks9",
    $$css: true
  },
  more: {
    kzqmXN: "xh8yej3",
    kAzted: "x1wiwyrm",
    kMzoRj: "xc342km",
    kEafiO: "x178xt8z",
    kPef9Z: "x13fuv20",
    kLZC3w: "x1m23mi5",
    kaIpWk: "x2u8bby",
    kWkggS: "xjbqb8w",
    kLKAdn: "xr5lh1o",
    kGO01o: "x18d9i69",
    kg3NbH: "xnjsko4",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1hu4l6k",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  loading: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1hu4l6k",
    $$css: true
  },
  loadingFrame: {
    kzqmXN: "xh8yej3",
    kOBAk4: "x1dltgaz",
    kaIpWk: "x1fv5t7x",
    kWkggS: "x1hueysq",
    $$css: true
  },
  fill: {
    k1xSpc: "x1lliihq",
    kZKoxP: "x5yr21d",
    kaIpWk: "xqhrb7l",
    kWkggS: "xfenp3h",
    k1ekBW: "xxrbq2n",
    kIyJzY: "x1d8287x",
    kAMwcw: "xcj1dhv",
    $$css: true
  },
  fillWidth: (percent: number) => [_temp, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${percent}%`)
  }],
  tile: {
    kzqmXN: "xh8yej3",
    kOBAk4: "x1of9k8c",
    kMzoRj: "xc342km",
    kaIpWk: "x1fv5t7x",
    kWkggS: "x1hueysq",
    kmVPX3: "x18ppelm",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x1cy8zhl",
    kjj79g: "xl56j7k",
    kOIVth: "x1c1vhfx",
    $$css: true
  }
};
export function TodaysPhoto({
  state,
  upload,
  qr,
  online,
  fromPhone,
  moments,
  defaultFailed = false,
  frame,
  onAdd,
  onChooseFiles,
  onCloseQr,
  onOpen,
  onOpenPhotos,
  xstyle
}: TodaysPhotoProps) {
  const ref = useRef<HTMLElement>(null);
  const narrow = (useBoxSize(ref)?.width ?? Number.POSITIVE_INFINITY) < NARROW_BOX;
  const shown = state.kind === "ready" && qr === undefined ? state.data[0] : undefined;
  return <section ref={ref} {...stylex.props(styles.panel, xstyle)}>
      <div className="x78zum5 x1qughib x6s0dn4 x152posx x1gg8mnh x1qj0mhr">
        <Eyebrow>{text.eyebrow}</Eyebrow>
        {shown && <Actions narrow={narrow} all={shown.others.length === 0} onAdd={onAdd} onOpenPhotos={onOpenPhotos} />}
      </div>
      {qr ? <QrPanel qr={qr} onChooseFiles={onChooseFiles} onCloseQr={onCloseQr} /> : <Zone state={state} loading={<Loading frame={frame} />} empty={upload ? <FirstUploadButton upload={upload} frame={frame} onOpenPhotos={onOpenPhotos} /> : <NoPhotos fromPhone={fromPhone} moments={moments} onAdd={onAdd} />} failed={null}>
          {([first]) => first && <PhotoBody shown={first} narrow={narrow} online={online} defaultFailed={defaultFailed} frame={frame} onOpen={onOpen} onOpenPhotos={onOpenPhotos} />}
        </Zone>}
    </section>;
}
function Actions({
  narrow,
  all,
  onAdd,
  onOpenPhotos
}: ActionsProps) {
  if (narrow) {
    return <div className="x78zum5 xt4j9g6">
        <IconButton icon={ImagePlus} label={text.addPhoto} variant="canvas" onClick={onAdd} xstyle={styles.iconButton} />
        {all && <IconButton icon={ArrowRight} label={text.allPhotos} variant="canvas" onClick={onOpenPhotos} xstyle={styles.iconButton} />}
      </div>;
  }
  return <div className="x78zum5 xt4j9g6">
      <Button variant="ghost" icon={ImagePlus} onClick={onAdd}>
        {text.addPhoto}
      </Button>
      {all && <Button variant="ghost" iconRight={ArrowRight} onClick={onOpenPhotos}>
          {text.allPhotos}
        </Button>}
    </div>;
}
function PhotoBody({
  shown,
  narrow,
  online,
  defaultFailed,
  frame,
  onOpen,
  onOpenPhotos
}: PhotoBodyProps) {
  const attempt = `${shown.src} ${online}`;
  const [failedAttempt, setFailedAttempt] = useState(defaultFailed ? attempt : undefined);
  const failed = failedAttempt === attempt;
  return <>
      <button type="button" aria-label={shown.text.name} onClick={() => onOpen(shown.photo.id)} {...stylex.props(styles.tap, styles.photo, press.scale)}>
        <span {...stylex.props(styles.frame, frame)}>
          {failed ? <span className="x10l6tqk x10a8y8t x78zum5 xdt5ytf x6s0dn4 xl56j7k x1hu4l6k x1vvitgl x9f619 x2b8uid">
              <Icon icon={ImageOff} size={32} strokeWidth={1.5} />
              <span className="xr0iwhn xif65rj xo1l8bm xbzybt3 xwn7fz2 xtvhhri">{online ? text.failed : text.offline}</span>
            </span> : <FrameImage key={shown.photo.id} src={shown.src} onError={() => setFailedAttempt(attempt)} />}
        </span>
        <span className="x78zum5 xdt5ytf xt4j9g6 x96kwfj xeuugli">
          <span {...{
          0: {
            className: "xosj86m x1cf7cre x132q4wb xuxw1ft xb3r6kr xlyipyv"
          },
          1: {
            className: "xosj86m x1cf7cre x132q4wb x104kibb x1h7i4cw x1ua5tub xb3r6kr"
          }
        }[!!narrow << 0]}>
            {shown.text.title}
          </span>
          {shown.text.caption && <span className="xr0iwhn xif65rj xo1l8bm xbzybt3 xwn7fz2 xtvhhri xuxw1ft xb3r6kr xlyipyv">{shown.text.caption}</span>}
        </span>
      </button>
      {shown.others.length > 0 && <MoreRow others={shown.others} onOpenPhotos={onOpenPhotos} />}
    </>;
}
function FrameImage({
  src,
  onError
}: FrameImageProps) {
  const [portraitSrc, setPortraitSrc] = useState<string>();
  const measure = useCallback((image: HTMLImageElement | null) => {
    if (image?.complete && image.naturalWidth > 0) setPortraitSrc(isPortrait(image) ? src : undefined);
  }, [src]);
  return (
    // biome-ignore lint/performance/noImgElement: photos come pre-sized from the authenticated file route and the service worker cache, not the Next image optimizer
    <img src={src} alt="" ref={measure} onLoad={event => measure(event.currentTarget)} onError={onError} {...{
      0: {
        className: "x10l6tqk x10a8y8t xh8yej3 x5yr21d xl1xv1r x1lliihq xqcmdr3 xrxw4wy xkxv0fq"
      },
      1: {
        className: "x10l6tqk x10a8y8t xh8yej3 x5yr21d xl1xv1r x1lliihq xqcmdr3 xrxw4wy xkxv0fq xq9q0n8"
      }
    }[!!(portraitSrc === src) << 0]} />
  );
}
function MoreRow({
  others,
  onOpenPhotos
}: MoreRowProps) {
  const label = text.more(others.length);
  return <div className="x96kwfj">
      <button type="button" aria-label={text.moreName(label)} onClick={onOpenPhotos} {...stylex.props(styles.more, press.scale)}>
        <span aria-hidden="true" className="x78zum5 x2lah0s">
          {others.slice(0, MAX_THUMBS).map(thumb =>
        // biome-ignore lint/performance/noImgElement: photos come pre-sized from the authenticated file route and the service worker cache, not the Next image optimizer
        <img key={thumb.id} src={thumb.src} alt="" className="xgd8bvy x1fgtraw xqhrb7l xl1xv1r x1lliihq x5r8ks9 xjmit6t x1qfufaz x1pwwqoy" />)}
        </span>
        <span className="x1iyjqo2 xeuugli x19d36u7 x1cf7cre">{label}</span>
        <Icon icon={ArrowRight} size={20} />
      </button>
    </div>;
}
function Loading({
  frame
}: {
  frame?: stylex.StyleXStyles;
}) {
  return <Skeleton label={text.loading} xstyle={styles.loading}>
      <div {...stylex.props(styles.loadingFrame, frame)} />
      <div className="x78zum5 xdt5ytf x152posx x96kwfj">
        <div className="x1i4uan3 x1kzsush x1kmanbg xmix8c7 x12oqio5" />
        <div className="x1i4uan3 x1kzsush x19h4ct x1kpxq89 x18zih8k" />
      </div>
    </Skeleton>;
}
function FirstUploadButton({
  upload,
  frame,
  onOpenPhotos
}: FirstUploadProps) {
  const name = text.upload.name(upload.percent);
  return <button type="button" aria-label={name} onClick={onOpenPhotos} {...stylex.props(styles.tap, styles.photo, press.scale)}>
      <span {...stylex.props(styles.frame, frame)}>
        {upload.preview &&
      // biome-ignore lint/performance/noImgElement: the preview is a local object URL of the file being uploaded
      <img src={upload.preview} alt="" className="x10l6tqk x10a8y8t xh8yej3 x5yr21d xl1xv1r x1lliihq xrxw4wy xkxv0fq x197sbye x1a5igra" />}
        <span role="progressbar" aria-label={name} aria-valuenow={upload.percent} aria-valuemin={0} aria-valuemax={100} className="x10l6tqk x1nl66qb x1yqgyd4 x10604ds xdk7pt xqhrb7l xh540kj xb3r6kr">
          <span {...stylex.props(styles.fill, styles.fillWidth(Math.max(upload.percent, MIN_BAR_PERCENT)))} />
        </span>
      </span>
      <span className="x78zum5 xdt5ytf xt4j9g6 x96kwfj xeuugli">
        <span className="xosj86m x1cf7cre x132q4wb">{text.upload.title}</span>
        <span className="xr0iwhn xif65rj xo1l8bm xbzybt3 xwn7fz2 xtvhhri xss6m8b">{upload.status}</span>
      </span>
    </button>;
}
function NoPhotos({
  fromPhone,
  moments,
  onAdd
}: NoPhotosProps) {
  return <button type="button" aria-label={fromPhone ? text.empty.fromPhoneName : text.empty.addPhotos} onClick={onAdd} {...stylex.props(styles.tap, styles.tile, press.scale)}>
      <span aria-hidden="true" className="x1n2onr6 x2lah0s x19zpy19 xwzfr38 x12fsci1 x1u7jqj0">
        <span className="x10l6tqk x10a8y8t x1wz39yh x14rdugl x1ysfl8e" />
        <span className="x10l6tqk x10a8y8t x1wz39yh xc0ge15 x1us7t46" />
        <span className="x10l6tqk x10a8y8t x1wz39yh xh540kj xuobv6a x1d74iyf x1ruevkc x9f619 x78zum5">
          <span className="x1iyjqo2 x18zih8k x1ubcl0s x78zum5 x6s0dn4 xl56j7k">
            <Icon icon={Image} size={28} strokeWidth={1.5} />
          </span>
        </span>
      </span>
      <span className="x78zum5 xdt5ytf xt4j9g6">
        <span className="x1i1m3gp x1v94iar x1tup8yo x1u7k74">{text.empty.title}</span>
        <span className="x1j61zf2 x15i68ds x37zpob x1fzhlzt">{moments}</span>
      </span>
      <span className="x3nfvp2 x6s0dn4 x152posx xn3w4p2 xizoatq x1xnnf8n xqhrb7l xh540kj x98nf4m x19d36u7 x1cf7cre">
        <Icon icon={fromPhone ? Smartphone : Upload} size={18} />
        {fromPhone ? text.empty.fromPhone : text.empty.addPhotos}
      </span>
    </button>;
}
function QrPanel({
  qr,
  onChooseFiles,
  onCloseQr
}: QrPanelProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset can't center its content as a flex column
    <div role="group" aria-label={text.qr.name} className="xh8yej3 x1of9k8c x1ubcl0s x1fv5t7x x1qhigcl x9f619 x78zum5 xdt5ytf xl56j7k x1c1vhfx">
      <div className="x78zum5 x1a02dak x6s0dn4 x1c1vhfx">
        <QrCode value={qr.url} label={text.qr.code} />
        <div className="x78zum5 xdt5ytf x152posx xfb3i0g x1iyjqo2 x1r8uery">
          <span className="xwsyq91 xfbjfs5 x1yrajzq">{text.qr.title}</span>
          <span className="x1jvydc1 x15i68ds xfrs9s4 x1fzhlzt">{text.qr.body}</span>
        </div>
      </div>
      <div className="x78zum5 x1a02dak x152posx">
        <Button variant="secondary" icon={Upload} onClick={onChooseFiles}>
          {text.qr.chooseFiles}
        </Button>
        <Button variant="ghost" icon={X} onClick={onCloseQr}>
          {text.qr.done}
        </Button>
      </div>
    </div>
  );
}
function isPortrait(image: HTMLImageElement): boolean {
  return image.naturalHeight > image.naturalWidth;
}